package com.arena_web.analytics

import com.arena_web.core.config.AppConfig
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlin.random.Random

/**
 * Fires the web-side "app download click" signal to our backend, which relays
 * it to Meta via the Conversions API (event `AppDownloadClick`). Store links
 * open in a new tab (target=_blank), so the current page stays alive and a plain
 * fetch completes; `keepalive` is belt-and-suspenders. Credentials are omitted —
 * fbclid/fbp travel in the body, so no cross-subdomain cookie is needed.
 */
object MetaCapiBeacon {

    /** Map a StorePill analyticsId to a platform and fire; no-op for non-download ids. */
    fun trackAppDownloadForAnalyticsId(analyticsId: String) {
        val platform = when (analyticsId) {
            "store_apple" -> "ios"
            "store_google" -> "android"
            else -> return // "store_google_soon" (toast, no download) or unrelated
        }
        send(platform)
    }

    private fun send(platform: String) {
        runCatching {
            val eventId = "dl_" + randomUuid()
            val fbclid = UtmCapture.fbclid()
            val fbp = UtmCapture.fbp()
            val body = buildString {
                append("{\"platform\":\"").append(platform).append("\",")
                append("\"eventId\":\"").append(eventId).append("\"")
                if (fbclid != null) append(",\"fbclid\":\"").append(fbclid).append("\"")
                if (fbp != null) append(",\"fbp\":\"").append(fbp).append("\"")
                append("}")
            }
            val url = AppConfig.getApiUrl("v1/track/app-download")
            val init = js("({})")
            init.method = "POST"
            init.keepalive = true
            val headers = js("({'Content-Type':'application/json'})")
            // Attach the bearer token when signed in so the backend can enrich the
            // CAPI event with the user's external_id (higher Event Match Quality).
            // The endpoint stays public — guests simply send no Authorization.
            Storage.get(StorageKeys.ACCESS_TOKEN)?.let { headers["Authorization"] = "Bearer $it" }
            init.headers = headers
            init.body = body
            window.fetch(url, init)
        }
    }

    private fun randomUuid(): String {
        val crypto = window.asDynamic().crypto
        if (crypto != null && crypto.randomUUID != undefined) {
            runCatching { return crypto.randomUUID() as String }
        }
        val hex = "0123456789abcdef"
        return buildString {
            repeat(36) { i ->
                when (i) {
                    8, 13, 18, 23 -> append('-')
                    14 -> append('4')
                    19 -> append(hex[Random.nextInt(4) or 8])
                    else -> append(hex[Random.nextInt(16)])
                }
            }
        }
    }
}
